import { cva } from "class-variance-authority";

import { BASE_ROLES, FILL_ROLES, FILL_ROLE_LINK_HOVERS } from "../../lib/colors";

/**
 * The roles Badge offers, and the source of both its `cva` keys and `BadgeProps["color"]` — so the
 * two cannot drift apart. Badge carries the status hues (`success`/`warning`/`info`) that Button
 * has no use for.
 */
export const BADGE_ROLES = [...BASE_ROLES, "success", "warning", "info", "ai"] as const;

export type BadgeColor = (typeof BADGE_ROLES)[number];

/** The two fill-less form factors, which share every role's ink and hover. */
const BADGE_OUTLINE_VARIANTS = ["outline", "dashed"] as const;

/**
 * Border + ink per role for the fill-less form factors. `secondary` and `muted` take Badge's plain
 * `border-border`; the rest carry their border from the registry's `outlineBase` — tinted, except
 * `warning`, whose yellow is too light to read as an edge and so is neutral there too.
 */
const BADGE_OUTLINE_BASE = {
  primary: FILL_ROLES.primary.outlineBase,
  secondary: `border-border ${FILL_ROLES.secondary.outlineBase}`,
  destructive: FILL_ROLES.destructive.outlineBase,
  muted: `border-border ${FILL_ROLES.muted.outlineBase}`,
  success: FILL_ROLES.success.outlineBase,
  warning: FILL_ROLES.warning.outlineBase,
  info: FILL_ROLES.info.outlineBase,
  white: FILL_ROLES.white.outlineBase,
  ai: FILL_ROLES.ai.outlineBase,
} as const satisfies Record<BadgeColor, string>;

// Migrated from @tellescope/ui/src/components/badge/styles.ts -- the `lab-*` variants (lab-high,
// lab-low, lab-normal, lab-recorded) were feature-specific to the laboratory results screen, not
// a general-purpose badge concern, so they're dropped rather than carried into this
// feature-agnostic package (per this package's no-feature-coupling rule). A lab result badge can
// still compose this Badge with a tone className of its own.
export const badgeVariants = cva(
  // structural
  "inline-flex w-fit shrink-0 items-center justify-center rounded-md " +
    // every variant reserves a 1px border box so switching form factor never shifts size or layout
    "border border-transparent " +
    "whitespace-nowrap transition-[color,box-shadow] " +
    // stateful
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 " +
    "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 " +
    // svg children — pointer-events only; per-size sizing lives in the `size` variant. `&&` doubles
    // the badge's class so these icon rules outrank a parent's same-specificity descendant rule
    // (e.g. Select.Item's `[&_svg]` size/color) when a badge is nested inside it.
    "[&>svg]:pointer-events-none [&&>svg:not([class*='text-'])]:text-current",
  {
    variants: {
      variant: {
        solid: "font-medium",
        soft: "font-medium",
        outline: "font-medium",
        dashed: "border-dashed bg-transparent font-normal",
      },
      size: {
        // Padding and type scale per size, plus the size of direct icon children — guarded on
        // `:not([class*='size-'])` so a consumer's explicit size-* on the icon still wins. The
        // `actionProps` button sizes itself via its own className (see Badge.tsx).
        // `leading-none` must sit *after* the `text-*` class in each of these strings: tailwind-
        // merge treats a font-size utility as also owning line-height, so a `leading-*` placed
        // earlier (e.g. in cva's base) gets stripped by the later `text-*`. Without it a badge
        // inherits its parent's leading -- 20px inside a `text-sm` tab -- which dominates the box
        // and reads as vertical padding.
        // `xs` is the one size off the type scale: `text-xs` is the smallest named step and `sm`
        // already uses it, so an arbitrary 10px is the only way for `xs` to read smaller than `sm`
        // in glyph size rather than only in box size. Deliberate -- revisit if a `2xs` type token
        // ever lands. It also drops to `rounded-sm`: at this height the base `rounded-md` (8px)
        // eats most of each edge and the badge reads as a full pill, not a rounded rectangle.
        xs: "gap-0.5 rounded-sm px-1 py-0.5 text-[10px] leading-none [&&>svg:not([class*='size-'])]:size-2",
        // `sm` stays at `text-xs` -- the smallest named step -- so it reads smaller than `md`
        // through tighter padding/gap and a lighter weight (see the `font-light` compound variant
        // below) instead.
        sm: "gap-0.5 px-1.5 py-0.5 text-xs leading-none [&&>svg:not([class*='size-'])]:size-2.5",
        md: "gap-1 px-2 py-1 text-xs leading-none [&&>svg:not([class*='size-'])]:size-3",
        lg: "gap-1.5 px-2.5 py-1.5 text-sm leading-none [&&>svg:not([class*='size-'])]:size-4",
      },
      // Keys only — every color pixel lives in the compoundVariants below, composed from the shared
      // registry. `satisfies` ties these keys to BadgeColor, so a role added to BADGE_ROLES without
      // a key here (or vice versa) is a type error rather than a silent gap.
      color: {
        primary: "",
        secondary: "",
        destructive: "",
        muted: "",
        success: "",
        warning: "",
        info: "",
        white: "",
        ai: "",
      } satisfies Record<BadgeColor, "">,
      hasAction: {
        // the trailing action button carries its own hit area, so the badge's right padding is
        // given back per size in the compound variants below. `overflow-hidden` is dropped here:
        // it would clip the action button's focus ring, which sits outside its box.
        true: "",
        false: "overflow-hidden",
      },
    },
    compoundVariants: [
      { hasAction: true, size: "xs", class: "pr-0.5" },
      { hasAction: true, size: "sm", class: "pr-0.5" },
      { hasAction: true, size: "md", class: "pr-0.5" },
      { hasAction: true, size: "lg", class: "pr-1" },
      // The small sizes override the weight every `variant` sets. This lives here rather than in
      // the `size` variant so it doesn't depend on `size` being declared after `variant` to win
      // the tailwind-merge font-weight conflict.
      { size: ["xs", "sm"], class: "font-light" },
      // solid — filled background, on-color foreground. Surface + ink come from the shared
      // registry; the hover is its `[a&]:`-gated form, since a badge is only interactive when it
      // renders as a link.
      ...BADGE_ROLES.map((color) => ({
        variant: "solid" as const,
        color,
        class: `${FILL_ROLES[color].solidBase} ${FILL_ROLE_LINK_HOVERS[color].solid}`,
      })),
      // soft — a quiet tinted fill in the role's own ink. Like `solid` it paints a surface, so it
      // takes the same `[a&]:`-gated hover treatment rather than the fill-less one below.
      ...BADGE_ROLES.map((color) => ({
        variant: "soft" as const,
        color,
        class: `${FILL_ROLES[color].softBase} ${FILL_ROLE_LINK_HOVERS[color].soft}`,
      })),
      // outline / dashed — no fill, so the registry's ink does double duty as text color, and the
      // border is Badge's own: `secondary` and `muted` share the plain `border-border` while the
      // rest carry their role's own tinted border from `outlineBase`.
      ...BADGE_OUTLINE_VARIANTS.flatMap((variant) =>
        BADGE_ROLES.map((color) => ({
          variant,
          color,
          class: `${BADGE_OUTLINE_BASE[color]} ${FILL_ROLE_LINK_HOVERS[color].outline}`,
        }))
      ),
    ],
    defaultVariants: {
      variant: "solid",
      color: "primary",
      size: "md",
      hasAction: false,
    },
  }
);
